// src/services/PaymentService.js
import { collection, addDoc, serverTimestamp, doc, updateDoc, arrayUnion, getDoc, increment } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { getUserStripeAccount } from './UserRolesService';

// Stripe publishable key - safe to use in client code
const STRIPE_PUBLISHABLE_KEY = process.env.NODE_ENV === 'production'
  ? 'pk_live_51MMCe7Egl6rkWI4KrrWfNpL17B3WGlzP7xv3Rq0BI7ppvRFK5yS3AQMa94HzFNkScTsyt5uJ7ckNMWf3GTBgOkF100KHOH7YCh'
  : process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_YOUR_TEST_KEY_HERE';

// Initialize Stripe
let stripePromise = null;
export const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

// Function to load Stripe dynamically
function loadStripe(key) {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = () => {
      resolve(window.Stripe(key));
    };
    document.head.appendChild(script);
  });
}

// Get the correct function URL based on environment
export const getFunctionURL = (functionName) => {
  if (process.env.REACT_APP_FUNCTIONS_URL) {
    return `${process.env.REACT_APP_FUNCTIONS_URL}/${functionName}`;
  }

  // Use the actual deployed URLs from Cloud Run
  const functionUrls = {
    createPaymentIntent: 'https://createpaymentintent-x27vmbsymq-uc.a.run.app',
    createConnectedAccount: 'https://createconnectedaccount-x27vmbsymq-uc.a.run.app',
    createAccountLink: 'https://createaccountlink-x27vmbsymq-uc.a.run.app',
    getAccountStatus: 'https://getaccountstatus-x27vmbsymq-uc.a.run.app',
    createDashboardLink: 'https://createdashboardlink-x27vmbsymq-uc.a.run.app',
    getListingInfo: 'https://getlistinginfo-x27vmbsymq-uc.a.run.app'
  };

  return functionUrls[functionName] || `https://us-central1-refounded-6a151.cloudfunctions.net/${functionName}`;
};

// Create a payment for a listing with platform fee
export const createPaymentIntent = async (listingId, amount, userId, listerEmail, quantity = 1) => {
  try {
    const currentUser = auth.currentUser;
    
    // Get the listing details to find the provider's user ID
    const listingRef = doc(db, 'listings', listingId);
    const listingDoc = await getDoc(listingRef);
    
    if (!listingDoc.exists()) {
      throw new Error('Listing not found');
    }
    
    const listingData = listingDoc.data();
    const providerId = listingData.userId;
    
    // Get the provider's Stripe account ID
    const providerStripeInfo = await getUserStripeAccount(providerId);
    const providerStripeAccountId = providerStripeInfo?.stripeAccountId;
    
    let payload = {
      amount,
      listingId,
      userId,
      listerEmail,
      providerId,
      providerStripeAccountId, // Include provider's Stripe account if they have one
      quantity
    };
    
    // Add ID token to the request if user is authenticated
    if (currentUser) {
      try {
        const idToken = await currentUser.getIdToken(true);
        payload.idToken = idToken;
      } catch (tokenError) {
        console.warn('Could not get ID token:', tokenError);
      }
    }
    
    const response = await fetch(getFunctionURL('createPaymentIntent'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Origin': window.location.origin
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Network response was not ok: ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating payment intent:', error);
    throw error;
  }
};

// Record the payment in Firestore (minimal info - webhook will update with full details)
export const recordPayment = async (paymentData) => {
  try {
    const { 
      listingId, 
      userId, 
      amount, 
      paymentIntentId, 
      status,
      listerEmail,
      quantity = 1
    } = paymentData;

    // Derive providerId from the listing so provider dashboards can query by providerId
    let providerId = null;
    try {
      if (listingId) {
        const listingRef = doc(db, 'listings', listingId);
        const listingSnap = await getDoc(listingRef);
        if (listingSnap.exists()) {
          const listingData = listingSnap.data();
          providerId = listingData.userId || null;
        }
      }
    } catch (e) {
      console.warn('Could not resolve providerId from listing; webhook will backfill.', e);
    }

    // Add a new payment record to the payments collection
    // The webhook will update this with provider split information
    const paymentRef = await addDoc(collection(db, 'payments'), {
      listingId,
      userId,
      amount,
      paymentIntentId,
      status,
      listerEmail,
      quantity,
      providerId,
      createdAt: serverTimestamp()
    });

    // If payment is successful, listing updates are handled server-side via webhook
    // This avoids client permission issues and ensures idempotency
    if (status === 'succeeded') {
      console.log('Payment succeeded; listing update will be applied by webhook for listing:', listingId);
    }

    return paymentRef.id;
  } catch (error) {
    console.error('Error recording payment:', error);
    throw error;
  }
};
